import { useCallback, useEffect, useRef, useState } from 'react';
import {
  requestSubstitutionApproval, fetchSubstitutionRequest,
} from './api';
import type { SubstitutionReview } from './types';

export type ApprovalState =
  | { phase: 'idle' }
  | { phase: 'resolved' } // dose mismatch, or a drug mismatch under non-blocking mode — nothing to wait for
  | { phase: 'pending'; request: SubstitutionReview }
  | { phase: 'approved'; request: SubstitutionReview }
  | { phase: 'rejected'; request: SubstitutionReview }
  | { phase: 'error'; message: string };

/**
 * Wraps the pre-checkout half of the drug-substitution approval workflow —
 * POST /pharmacy/substitutions/requests, then (only in blocking mode, only
 * for a genuine drug mismatch) polling GET /requests/:id until a doctor
 * decides. Shared between PharmacyStage.tsx and DispensingTab.tsx's
 * SubstitutePicker.onConfirm so the async "wait for approval" interaction
 * on a cart line is implemented once.
 *
 * A dose mismatch, or a drug mismatch under non-blocking mode, resolves
 * instantly (`phase: 'resolved'`) — the caller adds the cart line right
 * away, same as before this feature existed.
 */
export function useSubstitutionApproval() {
  const [state, setState] = useState<ApprovalState>({ phase: 'idle' });
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const poll = useCallback((requestId: number) => {
    pollRef.current = setTimeout(async () => {
      try {
        const request = await fetchSubstitutionRequest(requestId);
        if (request.status === 'approved') setState({ phase: 'approved', request });
        else if (request.status === 'rejected') setState({ phase: 'rejected', request });
        else { setState({ phase: 'pending', request }); poll(requestId); }
      } catch {
        // A failed poll retries rather than giving up — the pharmacist is
        // waiting on a real decision, and a transient network blip must not
        // read as "rejected."
        poll(requestId);
      }
    }, 5000);
  }, []);

  /** Call from SubstitutePicker's onConfirm, before adding the cart line. */
  const propose = useCallback(async (prescriptionItemId: number, dispensedInventoryId: number, reason: string) => {
    setState({ phase: 'idle' });
    try {
      const result = await requestSubstitutionApproval(prescriptionItemId, dispensedInventoryId, reason);
      if (!result.requires_approval || !result.request) {
        setState({ phase: 'resolved' });
        return;
      }
      if (result.request.status === 'approved') { setState({ phase: 'approved', request: result.request }); return; }
      setState({ phase: 'pending', request: result.request });
      poll(result.request.id);
    } catch (e) {
      setState({ phase: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }, [poll]);

  const reset = useCallback(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    setState({ phase: 'idle' });
  }, []);

  return { state, propose, reset };
}
